/**
 * lc-animations.js
 * Tech Tool — Line Card & Capabilities interaction system.
 *
 * Principles:
 *   - No external dependencies. Vanilla JS only.
 *   - prefers-reduced-motion is checked first; all motion is skipped if set.
 *   - Hero elements stagger on DOMContentLoaded (not scroll-triggered).
 *   - All other reveals use IntersectionObserver, fire once, then disconnect.
 *   - Section headers and key layout elements are auto-enrolled without
 *     requiring Liquid changes to every section file.
 *
 * Placement: assets/lc-animations.js
 * Load via: <script src="{{ 'lc-animations.js' | asset_url }}" defer></script>
 * Added to the bottom of lc-hero.liquid so it loads on both pages.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── If reduced motion, reveal everything immediately and exit ────────────────
  if (REDUCED) {
    function revealAll() {
      document.querySelectorAll('.lc-hero-reveal, .lc-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', revealAll);
    } else {
      revealAll();
    }
    return;
  }

  // ── Hero stagger ─────────────────────────────────────────────────────────────
  // Elements marked .lc-hero-reveal with data-lc-delay="0..4" reveal in sequence.
  // Delay index * 80ms = actual delay.
  function initHero() {
    document.querySelectorAll('.lc-hero-reveal').forEach(function (el) {
      var idx = parseInt(el.getAttribute('data-lc-delay') || '0', 10);
      var ms  = idx * 80;
      setTimeout(function () { el.classList.add('is-visible'); }, ms);
    });
  }

  // ── Auto-enroll common section elements ──────────────────────────────────────
  // These selectors exist across all sections without Liquid changes.
  // JS adds .lc-reveal so IntersectionObserver picks them up below.
  var AUTO_REVEAL = [
    '.lc-section-header',
    '.lc-cap-richtext__inner',
    '.lc-proof__statement',
    '.lc-footer-cta__inner',
    '.lc-sourcing__grid',
    '.lc-form',
    '.lc-what-to-bring__inner',
    '.lc-compare__grid',
    '.lc-sourcing-path__header'
  ];

  function initAutoReveal() {
    AUTO_REVEAL.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (!el.classList.contains('lc-reveal') && !el.classList.contains('lc-hero-reveal')) {
          el.classList.add('lc-reveal');
        }
      });
    });
  }

  // ── IntersectionObserver scroll reveal ───────────────────────────────────────
  // Triggers once. Fires at 8% visibility with a 32px bottom margin so elements
  // appear just before they fully enter the viewport — feels immediate, not delayed.
  function initScrollReveal() {
    var els = document.querySelectorAll('.lc-reveal');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      // Graceful fallback for very old browsers: show everything
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target); // fire once only
        }
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -32px 0px'
    });

    els.forEach(function (el) { io.observe(el); });
  }

  // ── Init on DOMContentLoaded ─────────────────────────────────────────────────
  function init() {
    initHero();
    initAutoReveal();   // must run before initScrollReveal
    initScrollReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
